import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuthContext } from '../context/AuthContext';
import { AuthShell, AuthButton, AuthAlert, authInputClass, authLabelClass } from '../components/AuthShell';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const { forgotPassword } = useAuthContext();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setMessage('Please enter your email address');
      setIsSuccess(false);
      return;
    }
    setIsSubmitting(true);
    setMessage(null);
    try {
      const result = await forgotPassword({ email });
      if (result.success) {
        setIsSuccess(true);
        setMessage('Password reset instructions have been sent to your email');
      } else {
        setIsSuccess(false);
        setMessage(result.error || 'Failed to send password reset email. Please try again.');
      }
    } catch (error: any) {
      setIsSuccess(false);
      setMessage(error.message || 'Failed to send password reset email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO title="Forgot Password" description="Reset your password to regain access to your account" keywords="forgot password, reset password" />

      <AuthShell
        title={isSuccess ? 'Check your email' : 'Forgot your password?'}
        subtitle={isSuccess ? undefined : 'Enter your email and we’ll send you a link to reset it.'}
        footer={
          <>
            Remember your password?{' '}
            <Link to="/auth/login" className="font-semibold text-inkblack no-underline hover:text-inkblack">
              Back to sign in
            </Link>
          </>
        }
      >
        {isSuccess ? (
          <div className="space-y-5">
            <AuthAlert tone="success" title="Reset link sent">{message}</AuthAlert>
            <p className="text-sm text-inkblack-60">
              If you don&rsquo;t receive an email within a few minutes, check your spam folder.
            </p>
            <button
              onClick={() => { setIsSuccess(false); setMessage(null); setEmail(''); }}
              className="inline-flex w-full items-center justify-center rounded-xl border border-receipt bg-paper px-4 py-2.5 text-sm font-semibold text-inkblack-80 transition hover:border-receipt hover:bg-receipt-40"
            >
              Send another email
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {message && !isSuccess && <AuthAlert title="Something went wrong" onClose={() => setMessage(null)}>{message}</AuthAlert>}

            <div className="space-y-1.5">
              <label htmlFor="email" className={authLabelClass}>Email address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-inkblack-60" />
                <input id="email" name="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={authInputClass} placeholder="you@professional.com" />
              </div>
            </div>

            <AuthButton type="submit" loading={isSubmitting}>
              {isSubmitting ? 'Sending reset link…' : (<>Send reset link <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>)}
            </AuthButton>
          </form>
        )}
      </AuthShell>
    </>
  );
};

export default ForgotPasswordPage;
